import { createSlice } from "@reduxjs/toolkit";
import { HOME_CAROUSELS } from "../screens/home/carouselTypes";

// Session cache for the home carousels. Holding the rows in the store rather
// than in the screen's own state means a return trip to home paints instantly
// and costs no requests — the single GetCarousels call fires once per page load.
//
// Invalidated by a location change (the rows are distance-ranked), by an
// explicit retry, and by a hard refresh, which rebuilds the store.
const loadingRows = () =>
  HOME_CAROUSELS.reduce((acc, { key }) => {
    acc[key] = { status: "loading", items: [] };
    return acc;
  }, {});

export const slice = createSlice({
  name: "homeCarouselsSlice",
  initialState: {
    rows: loadingRows(),
    // The locationKey the cached rows were fetched for; null = never fetched.
    fetchedForLocation: null,
  },
  reducers: {
    carouselsRequested: (state, action) => {
      state.rows = loadingRows();
      state.fetchedForLocation = action.payload;
    },
    // Same round, minus the reset: claims the new locationKey but leaves the rows
    // standing so the fresh data swaps in place. Used when a location we already
    // fetched for is superseded — the welcome prefetch runs on the saved address
    // and the GPS fix lands a moment later — where blanking populated rows
    // back to skeletons would be a visible regression, not a loading state.
    carouselsRevalidated: (state, action) => {
      state.fetchedForLocation = action.payload;
    },
    // The whole round lands at once: every row becomes ready. Rows the response
    // didn't include arrive as empty items — the row hides itself.
    carouselsLoaded: (state, action) => {
      state.rows = action.payload.rows;
    },
    // One request feeds every row, so failure is all-or-nothing by construction.
    carouselsFailed: (state) => {
      Object.keys(state.rows).forEach((key) => {
        state.rows[key] = { status: "error", items: [] };
      });
    },
    carouselsInvalidated: (state) => {
      state.rows = loadingRows();
      state.fetchedForLocation = null;
    },
  },
});

export const { carouselsRequested, carouselsRevalidated, carouselsLoaded, carouselsFailed, carouselsInvalidated } =
  slice.actions;

export const selectCarouselRows = (state) => state.homeCarouselsSlice.rows;
export const selectCarouselsFetchedFor = (state) => state.homeCarouselsSlice.fetchedForLocation;

export default slice.reducer;
